import { useCurrentNavigationValues } from '@innovamat/ga-features';
import type { ClassroomTestRoundDetailQuery } from '@innovamat/glow-api-client';
import { useClassroomTestRoundDetailQuery } from '@innovamat/glow-api-client';
import { useEffect, useState } from 'react';
import { useConmatTestDetail } from '../../../../hooks/use-conmat-test-detail';
import { useCosmosTestDetail } from '../../../../hooks/use-cosmos-test-detail';
import type { ConmatTestDetail } from '../../../../modules/report/conmat-test-detail';
import type { CosmosTestDetail } from '../../../../modules/report/cosmos-test-detail';
import type { TestType } from '../../../../modules/test/test';
import { TEST_TYPE } from '../../../../modules/test/test';
import { formatDate } from '../../../../utils/date';
import { getFaketestsAndResultsMockDataDetailsData } from '../../mock-fake-data';
import { useTestAndResults } from './context/test-and-results-provider';

type DetailData =
  | ClassroomTestRoundDetailQuery['classroomTestRoundDetail']
  | CosmosTestDetail
  | ConmatTestDetail
  | null;

type UseTestAndResultsDrawer = {
  detailData: DetailData;
  formattedDate: string;
  formattedParticipation: string;
  isError: boolean;
  isLoading: boolean;
  lastSelectedType: TestType | undefined;
  handleDrawerClose: () => void;
};

function useTestAndResultsDrawer(): UseTestAndResultsDrawer {
  const { currentClassroom } = useCurrentNavigationValues();
  const { drawerId, rowSelected, onCloseDrawer } = useTestAndResults();

  const [lastSelectedType, setLastSelectedType] = useState<
    TestType | undefined
  >();

  const isFakeData = drawerId?.includes('fake-data');

  const {
    data: mockData,
    isLoading: isMockExamsLoading,
    isError: isMockExamsError,
  } = useClassroomTestRoundDetailQuery(
    {
      testRoundId: drawerId ?? '',
      classroomId: currentClassroom?.id ?? '',
    },
    {
      enabled:
        !!drawerId && !isFakeData && rowSelected?.type === TEST_TYPE.MOCK,
      staleTime: Infinity,
    }
  );

  const {
    data: conmatTestDetail,
    isLoading: isConmatTestDetailLoading,
    error: isConmatTestDetailError,
  } = useConmatTestDetail({
    testClassroomUuid: drawerId ?? '',
    classroomId: currentClassroom?.id ?? '',
    enabled: !isFakeData && rowSelected?.type === TEST_TYPE.CONMAT,
  });

  const {
    data: cosmosTestDetail,
    isLoading: isCosmosTestDetailLoading,
    error: isCosmosTestDetailError,
  } = useCosmosTestDetail({
    testClassroomUuid: drawerId ?? '',
    classroomId: currentClassroom?.id ?? '',
    enabled: !isFakeData && rowSelected?.type === TEST_TYPE.COSMOS,
  });

  const getDetailData = (): DetailData => {
    if (isFakeData) {
      return getFaketestsAndResultsMockDataDetailsData(drawerId ?? '')
        .classroomTestRoundDetail;
    }

    if (rowSelected?.type === TEST_TYPE.MOCK) {
      return mockData?.classroomTestRoundDetail;
    }

    if (rowSelected?.type === TEST_TYPE.COSMOS) {
      return cosmosTestDetail;
    }

    if (rowSelected?.type === TEST_TYPE.CONMAT) {
      return conmatTestDetail;
    }

    return getFaketestsAndResultsMockDataDetailsData(drawerId ?? '')
      .classroomTestRoundDetail;
  };

  const detailData = getDetailData();
  const isLoading =
    isMockExamsLoading ||
    isConmatTestDetailLoading ||
    isCosmosTestDetailLoading;
  const isError = Boolean(
    isMockExamsError || isConmatTestDetailError || isCosmosTestDetailError
  );

  const formattedDate = `${formatDate(
    detailData?.startDate ?? ''
  )} - ${formatDate(detailData?.endDate ?? '')}`;

  const formattedParticipation = `${detailData?.participation?.completed} / ${
    currentClassroom?.numberOfStudents ?? 0
  }`;

  const handleDrawerClose = (): void => {
    onCloseDrawer();
  };

  useEffect(() => {
    if (rowSelected?.type) {
      setLastSelectedType(rowSelected.type);
    }
  }, [rowSelected?.type]);

  return {
    detailData,
    formattedDate,
    formattedParticipation,
    isError,
    isLoading,
    lastSelectedType,
    handleDrawerClose,
  };
}

export { useTestAndResultsDrawer };
export type { DetailData };
